import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    catchError,
    finalize,
    map,
    of,
    shareReplay,
    tap,
} from 'rxjs';
import { UpdateRequiredOrder } from './updates-required.types';
import { ApiService } from 'app/core/services/api.service';
import { OrdersService } from 'app/modules/admin/orders/orders.service';
import { isPaymentSatisfiedForProduction } from 'app/modules/admin/orders/order-checkpoint-display';
import { isAwaitingHandoff, isAwaitingAssigneeHandoff } from 'app/modules/admin/orders/order-handoff';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class UpdatesRequiredService {
    private _issueOrders: BehaviorSubject<UpdateRequiredOrder[]> = new BehaviorSubject<UpdateRequiredOrder[]>([]);
    private _issueOrdersCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private _currentSlideIndex: number = 0;
    private _issueOrdersRequest$: Observable<UpdateRequiredOrder[]> | null = null;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _apiService: ApiService,
        private _ordersService: OrdersService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for issue orders
     */
    get issueOrders$(): Observable<UpdateRequiredOrder[]> {
        return this._issueOrders.asObservable();
    }

    /**
     * Getter for issue orders count
     */
    get issueOrdersCount$(): Observable<number> {
        return this._issueOrdersCount.asObservable();
    }

    /**
     * Getter for current slide index
     */
    get currentSlideIndex(): number {
        return this._currentSlideIndex;
    }

    /**
     * Setter for current slide index
     */
    set currentSlideIndex(index: number) {
        this._currentSlideIndex = index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all orders with issues
     * Uses the slim dashboard shape, then filters client-side with all
     * update-required scenarios. Concurrent layout callers share one request.
     */
    getUpdateIssueOrders(): Observable<UpdateRequiredOrder[]> {
        if (this._issueOrdersRequest$) {
            return this._issueOrdersRequest$;
        }

        const request$ = this._ordersService
            .getAllOrdersSlim('type=updates_required&paginate=50')
            .pipe(
                map((response: any) => {
                    const allOrders = response?.data || [];
                    // Apply the 6-scenario filter
                    const filtered = this.filterUpdatesRequired(allOrders);
                    // Add issue status labels
                    return this.formatOrderList(filtered);
                }),
                tap((orders: UpdateRequiredOrder[]) => {
                    this._issueOrders.next(orders);
                    this._issueOrdersCount.next(orders.length);
                }),
                catchError(error => {
                    console.error('Error fetching updates required data:', error);
                    this._issueOrders.next([]);
                    this._issueOrdersCount.next(0);
                    return of([]);
                }),
                finalize(() => {
                    if (this._issueOrdersRequest$ === request$) {
                        this._issueOrdersRequest$ = null;
                    }
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );

        this._issueOrdersRequest$ = request$;
        return request$;
    }

    /**
     * Format order list with issue status labels
     */
    private formatOrderList(orderListArray: any[]): UpdateRequiredOrder[] {
        return orderListArray.map(item => {
            item.issueStatus = this.issueStatus(item);
            return item;
        });
    }

    // =========================================================================
    // Shared helpers (same logic as OperationsDashboardService)
    // =========================================================================

    private isTruthy(v: any): boolean {
        return v === 1 || v === true || v === '1' || v === 'true';
    }

    private isMissingCheckpoints(order: any): boolean {
        const status = order.status || '';
        const payOk = isPaymentSatisfiedForProduction(order) || status === 'Production';
        const artOk = this.isTruthy(order.art_is_sized);
        const filmOk = this.isTruthy(order.film);
        const apparelOk = this.isTruthy(order.apparel);
        return !(payOk && artOk && filmOk && apparelOk);
    }

    private getDaysUntilDue(arrivalDate: string | null | undefined): number | null {
        if (!arrivalDate) return null;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Parse as local date — "2026-02-12" must not be treated as UTC
        const dateStr = String(arrivalDate);
        const parts = dateStr.split(/[-T]/);
        let dueDay: Date;
        if (parts.length >= 3) {
            dueDay = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
            const parsed = new Date(arrivalDate);
            dueDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        }

        return Math.round((dueDay.getTime() - today.getTime()) / (1000 * 3600 * 24));
    }

    private isOverdue(order: any): boolean {
        const days = this.getDaysUntilDue(order.arrival_date);
        return days !== null && days < 0;
    }

    /**
     * Filter orders matching update-required scenarios:
     * 1. Processing or Production + overdue
     * 2. Any status + issue_with_order flagged
     * 3. Completed + overdue + not shipped (not a handoff path)
     * 4. Production + missing admin checkpoints
     * 5. Overdue + awaiting DP handoff (creator or assignee)
     */
    private filterUpdatesRequired(orders: any[]): any[] {
        return orders.filter((o: any) => {
            const status = o.status || '';
            const missingCheckpoints = this.isMissingCheckpoints(o);
            const overdue = this.isOverdue(o);
            const isNotShipped = !o.actual_shipped && !o.shipped && !o.is_shipped;
            const hasIssue = o.issue_with_order?.is_issue === 'true' || o.issue_with_order?.is_issue === true;
            const awaitingHandoff = isAwaitingHandoff(o) || isAwaitingAssigneeHandoff(o);

            if ((status === 'Processing' || status === 'Production') && overdue) return true;
            if (hasIssue) return true;
            if (awaitingHandoff && overdue) return true;
            if (status === 'Completed' && overdue && isNotShipped && !awaitingHandoff) return true;
            if (status === 'Production' && missingCheckpoints) return true;

            return false;
        });
    }

    /**
     * Determine issue status label and hover text for an order
     * Updated to match all 6 scenarios
     */
    private issueStatus(item: any): { text: string; hover: string } {
        const days = this.getDaysUntilDue(item.arrival_date);
        const overdue = days !== null && days < 0;
        const approaching = days !== null && days >= 0 && days <= 2;
        const missingCheckpoints = this.isMissingCheckpoints(item);
        const isNotShipped = !item.actual_shipped && !item.shipped && !item.is_shipped;
        const hasIssue = item.issue_with_order?.is_issue === 'true' || item.issue_with_order?.is_issue === true;

        // Build missing items list
        const missing: string[] = [];
        if (!isPaymentSatisfiedForProduction(item) && item.status !== 'Production') missing.push('Payment');
        if (!this.isTruthy(item.art_is_sized)) missing.push('Art');
        if (!this.isTruthy(item.film)) missing.push('Film');
        if (!this.isTruthy(item.apparel)) missing.push('Merch');
        const missingText = missing.join(', ');
        const awaitingCreatorHandoff = isAwaitingHandoff(item);
        const awaitingAssigneeHandoff = isAwaitingAssigneeHandoff(item);

        if (hasIssue) {
            return {
                text: 'Order Marked Incomplete',
                hover: 'Something is missing. Please check the Order'
            };
        }

        if (awaitingCreatorHandoff) {
            return {
                text: overdue ? 'Overdue — needs handoff' : 'Needs handoff',
                hover: 'Hand off finished work back to the creator'
            };
        }

        if (awaitingAssigneeHandoff) {
            return {
                text: overdue ? 'Overdue — needs assignee handoff' : 'Needs assignee handoff',
                hover: 'Hand off merchandise to the outside resource or trader'
            };
        }

        if (item.status === 'Completed' && overdue && isNotShipped) {
            const fulfillmentLabel = this.getAwaitingFulfillmentLabel(item);
            return {
                text: `Overdue — ${fulfillmentLabel}`,
                hover: fulfillmentLabel === 'awaiting pickup'
                    ? 'Order is past due and is still awaiting customer pickup'
                    : fulfillmentLabel === 'awaiting delivery'
                        ? 'Order is past due and is still awaiting local delivery'
                        : 'Order is past due date and has not been shipped'
            };
        }

        // Scenario 3: Overdue (Processing or Production)
        if ((item.status === 'Processing' || item.status === 'Production') && overdue) {
            const missingLabel = missing.map(value => `Missing ${value}`).join(', ');
            return {
                text: missingCheckpoints ? missingLabel : 'Overdue',
                hover: missingCheckpoints
                    ? `Order is overdue and still needs: ${missingText}`
                    : 'Due date has passed; update the due date'
            };
        }

        // Scenario 2: Approaching due date + missing checkpoints
        if (item.status === 'Processing' && approaching && missingCheckpoints) {
            return {
                text: 'Due Date approaching',
                hover: `Missing: ${missingText}`
            };
        }

        // Scenario 1 & 6: Missing checkpoints (Processing or Production)
        if (missingCheckpoints) {
            return {
                text: missing.map(value => `Missing ${value}`).join(', '),
                hover: `Order needs: ${missingText}`
            };
        }

        return { text: '', hover: '' };
    }

    /**
     * Describe the outstanding fulfillment action without calling a pickup
     * or local delivery "not shipped".
     */
    private getAwaitingFulfillmentLabel(item: any): string {
        const shipType = String(item?.ship_type || '').toLowerCase().trim();

        if (
            shipType.includes('pickup') ||
            shipType === 'hold_at_shop' ||
            shipType === 'sample_for_hold'
        ) {
            return 'awaiting pickup';
        }

        if (['courier', 'uber', 'hand_deliver', 'local_delivery'].includes(shipType)) {
            return 'awaiting delivery';
        }

        return 'not shipped';
    }

    /**
     * Move to next slide
     */
    nextSlide(): void {
        const orders = this._issueOrders.value;
        if (orders.length === 0) {
            return;
        }

        if (this._currentSlideIndex < orders.length - 1) {
            this._currentSlideIndex++;
        } else {
            this._currentSlideIndex = 0;
        }
    }

    /**
     * Move to previous slide
     */
    prevSlide(): void {
        const orders = this._issueOrders.value;
        if (orders.length === 0) {
            return;
        }

        if (this._currentSlideIndex > 0) {
            this._currentSlideIndex--;
        } else {
            this._currentSlideIndex = orders.length - 1;
        }
    }

    /**
     * Get mock data for development/testing
     * This simulates the API response with sample data
     */
    getMockIssueOrders(): void {
        const mockData: UpdateRequiredOrder[] = [
            {
                id: '1001',
                status: 'Processing',
                name: 'Website Redesign Project',
                issueStatus: {
                    text: 'Missing approval',
                    hover: 'Client approval needed before proceeding'
                }
            },
            {
                id: '1002',
                status: 'Production',
                name: 'E-commerce Platform Integration',
                issueStatus: {
                    text: 'Payment method issue',
                    hover: 'Payment gateway configuration needed'
                }
            },
            {
                id: '1003',
                status: 'Production',
                name: 'Mobile App Development',
                issueStatus: {
                    text: 'API access error',
                    hover: 'Backend API credentials expired'
                }
            }
        ];

        this._issueOrders.next(mockData);
        this._issueOrdersCount.next(mockData.length);
    }
}